/**
 * Sales Dossier Service
 * 
 * Generates comprehensive sales dossiers from Customer Intelligence data.
 * Used for customer acquisition and sales pitches.
 * 
 * A dossier includes:
 * - Business overview and pain points
 * - Technical issues (PageSpeed, etc.)
 * - Competitive landscape
 * - Brand analysis
 * - Recommended solutions
 * - Example assets (images, ad copy)
 */

import { customerIntelligenceService } from './customerIntelligenceService';
import { geminiService } from './geminiService';
import { gammaService } from './gammaService';
import type { CustomerIntelligence } from '../types/customerIntelligence';

// ============================================================================
// TYPES
// ============================================================================

export interface SalesDossier {
  id: string;
  createdAt: Date;
  
  // Target business
  businessName: string;
  domain: string;
  industry: string;
  
  // Executive Summary
  executiveSummary: string;
  
  // Pain Points identified
  painPoints: PainPoint[];
  
  // Opportunities
  opportunities: Opportunity[];
  
  // Competitive Analysis Summary
  competitiveInsights: {
    competitors: string[];
    marketPosition: string;
    differentiators: string[];
  };
  
  // Brand Analysis
  brandAnalysis: {
    currentVoice: string;
    visualIdentity: string;
    strengths: string[];
    improvementAreas: string[];
  };
  
  // Recommended Solutions
  recommendations: Recommendation[];
  
  // Example Assets (generated)
  exampleAssets?: {
    adCopy?: {
      headlines: string[];
      descriptions: string[];
    };
    imagePrompts?: string[];
  };
  
  // Presentation link (if generated)
  presentationUrl?: string;
}

export interface PainPoint {
  category: 'technical' | 'marketing' | 'brand' | 'competitive' | 'conversion';
  severity: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  evidence: string;
  impact: string;
}

export interface Opportunity {
  category: 'google-ads' | 'seo' | 'content' | 'branding' | 'conversion';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  expectedImpact: string;
}

export interface Recommendation {
  service: string;
  description: string;
  benefits: string[];
  estimatedImpact: string;
  priority: 'immediate' | 'short-term' | 'long-term';
}

export interface DossierOptions {
  language?: 'de' | 'en';
  includeExamples?: boolean;
  includePresentation?: boolean;
  focusAreas?: ('google-ads' | 'seo' | 'branding' | 'conversion')[];
}

// ============================================================================
// MAIN SERVICE
// ============================================================================

export const salesDossierService = {
  /**
   * Generate a comprehensive sales dossier from Customer Intelligence
   */
  async generateDossier(
    orgId: string,
    ciId: string,
    options: DossierOptions = {}
  ): Promise<SalesDossier> {
    const language = options.language || 'de';
    
    // Get Customer Intelligence data
    const ci = await customerIntelligenceService.getById(orgId, ciId);
    if (!ci) {
      throw new Error(`Customer Intelligence not found: ${ciId}`);
    }
    
    console.log(`📋 Generating sales dossier for: ${ci.businessName}`);
    
    // Generate each section
    const [painPoints, opportunities, recommendations] = await Promise.all([
      this.identifyPainPoints(ci, language),
      this.identifyOpportunities(ci, language),
      this.generateRecommendations(ci, language, options.focusAreas)
    ]);
    
    // Generate executive summary
    const executiveSummary = await this.generateExecutiveSummary(
      ci, 
      painPoints, 
      opportunities, 
      language
    );
    
    // Build brand analysis
    const brandAnalysis = this.buildBrandAnalysis(ci);
    
    // Build competitive insights
    const competitiveInsights = this.buildCompetitiveInsights(ci);
    
    // Generate example assets if requested
    let exampleAssets;
    if (options.includeExamples) {
      exampleAssets = await this.generateExampleAssets(ci, language);
    }
    
    const dossier: SalesDossier = {
      id: `dossier-${Date.now()}`,
      createdAt: new Date(),
      businessName: ci.businessName,
      domain: ci.domain,
      industry: ci.brand.industry,
      executiveSummary,
      painPoints,
      opportunities,
      competitiveInsights,
      brandAnalysis,
      recommendations,
      exampleAssets
    };
    
    // Generate presentation if requested
    if (options.includePresentation) {
      try {
        const presentation = await gammaService.createPresentation(ci, { language });
        dossier.presentationUrl = presentation.url;
      } catch (e) {
        console.warn('Failed to generate presentation:', e);
      }
    }
    
    console.log(`✅ Sales dossier generated with ${painPoints.length} pain points and ${recommendations.length} recommendations`);
    
    return dossier;
  },

  /**
   * Identify pain points from CI data
   */
  async identifyPainPoints(
    ci: CustomerIntelligence,
    language: 'de' | 'en'
  ): Promise<PainPoint[]> {
    const painPoints: PainPoint[] = [];
    
    // Technical pain points from PageSpeed
    if (ci.performance?.pageSpeed) {
      const ps = ci.performance.pageSpeed;
      
      if (ps.performanceScore && ps.performanceScore < 50) {
        painPoints.push({
          category: 'technical',
          severity: 'high',
          title: language === 'de' ? 'Kritische Website-Performance' : 'Critical Website Performance',
          description: language === 'de' 
            ? `Die Website hat einen Performance-Score von nur ${ps.performanceScore}/100.`
            : `The website has a performance score of only ${ps.performanceScore}/100.`,
          evidence: `PageSpeed Score: ${ps.performanceScore}`,
          impact: language === 'de'
            ? 'Langsame Websites verlieren bis zu 40% der Besucher'
            : 'Slow websites lose up to 40% of visitors'
        });
      }
      
      if (ps.largestContentfulPaint && ps.largestContentfulPaint > 2500) {
        painPoints.push({
          category: 'technical',
          severity: 'medium',
          title: language === 'de' ? 'Langsame Ladezeit' : 'Slow Load Time',
          description: language === 'de'
            ? `LCP von ${(ps.largestContentfulPaint / 1000).toFixed(1)}s überschreitet den Google-Richtwert von 2.5s`
            : `LCP of ${(ps.largestContentfulPaint / 1000).toFixed(1)}s exceeds Google's 2.5s threshold`,
          evidence: `LCP: ${ps.largestContentfulPaint}ms`,
          impact: language === 'de'
            ? 'Negative Auswirkung auf SEO-Rankings und Conversion-Rate'
            : 'Negative impact on SEO rankings and conversion rate'
        });
      }
    }
    
    // Marketing pain points
    if (ci.integrations?.googleAds) {
      const hasGoogleAds = ci.integrations.googleAds.isConnected;
      if (hasGoogleAds) {
        painPoints.push({
          category: 'marketing',
          severity: 'medium',
          title: language === 'de' ? 'Google Ads vorhanden - Optimierungspotential' : 'Google Ads Present - Optimization Potential',
          description: language === 'de'
            ? 'Der Kunde nutzt bereits Google Ads. Hier besteht Potential für Optimierung und bessere ROI.'
            : 'Customer already uses Google Ads. There is potential for optimization and better ROI.',
          evidence: 'Google Ads Tag detected',
          impact: language === 'de'
            ? 'Unoptimierte Kampagnen verschwenden Budget'
            : 'Unoptimized campaigns waste budget'
        });
      }
    }
    
    // Brand pain points
    if (ci.voice?.confidence && ci.voice.confidence < 60) {
      painPoints.push({
        category: 'brand',
        severity: 'low',
        title: language === 'de' ? 'Inkonsistente Markensprache' : 'Inconsistent Brand Voice',
        description: language === 'de'
          ? 'Die Tonalität der Website ist nicht klar definiert.'
          : 'The website tone of voice is not clearly defined.',
        evidence: `Voice Confidence: ${ci.voice.confidence}%`,
        impact: language === 'de'
          ? 'Schwächere Markenwahrnehmung und Kundenbindung'
          : 'Weaker brand perception and customer loyalty'
      });
    }
    
    // Competitive pain points
    if (ci.competitors && ci.competitors.length > 0) {
      painPoints.push({
        category: 'competitive',
        severity: 'medium',
        title: language === 'de' ? 'Starker Wettbewerb' : 'Strong Competition',
        description: language === 'de'
          ? `${ci.competitors.length} direkte Wettbewerber in der Region identifiziert.`
          : `${ci.competitors.length} direct competitors identified in the region.`,
        evidence: `Competitors: ${ci.competitors.slice(0, 3).map((c: any) => c.name).join(', ')}`,
        impact: language === 'de'
          ? 'Differenzierung und gezielte Positionierung notwendig'
          : 'Differentiation and targeted positioning required'
      });
    }
    
    return painPoints;
  },

  /**
   * Identify opportunities from CI data
   */
  async identifyOpportunities(
    ci: CustomerIntelligence,
    language: 'de' | 'en'
  ): Promise<Opportunity[]> {
    const opportunities: Opportunity[] = [];
    
    // Google Ads opportunity
    opportunities.push({
      category: 'google-ads',
      priority: 'high',
      title: language === 'de' ? 'Google Ads Kampagnen' : 'Google Ads Campaigns',
      description: language === 'de'
        ? `Gezielte Search und Performance Max Kampagnen für ${ci.brand.industry}`
        : `Targeted Search and Performance Max campaigns for ${ci.brand.industry}`,
      expectedImpact: language === 'de'
        ? 'Steigerung qualifizierter Leads um 30-50%'
        : '30-50% increase in qualified leads'
    });
    
    // SEO opportunity if PageSpeed issues
    if (ci.performance?.pageSpeed?.performanceScore && ci.performance.pageSpeed.performanceScore < 70) {
      opportunities.push({
        category: 'seo',
        priority: 'high',
        title: language === 'de' ? 'Website-Optimierung' : 'Website Optimization',
        description: language === 'de'
          ? 'Technische Optimierung für bessere Rankings und User Experience'
          : 'Technical optimization for better rankings and user experience',
        expectedImpact: language === 'de'
          ? 'Bis zu 20% mehr organischer Traffic'
          : 'Up to 20% more organic traffic'
      });
    }
    
    // Content opportunity
    if (ci.keywords?.core && ci.keywords.core.length > 5) {
      opportunities.push({
        category: 'content',
        priority: 'medium',
        title: language === 'de' ? 'Content-Strategie' : 'Content Strategy',
        description: language === 'de'
          ? `${ci.keywords.core.length} relevante Keywords für Content-Erstellung identifiziert`
          : `${ci.keywords.core.length} relevant keywords identified for content creation`,
        expectedImpact: language === 'de'
          ? 'Stärkere organische Sichtbarkeit'
          : 'Stronger organic visibility'
      });
    }
    
    // Branding opportunity
    opportunities.push({
      category: 'branding',
      priority: 'medium',
      title: language === 'de' ? 'Markenpositionierung' : 'Brand Positioning',
      description: language === 'de'
        ? 'Konsistente Markensprache über alle Kanäle'
        : 'Consistent brand voice across all channels',
      expectedImpact: language === 'de'
        ? 'Höhere Markenbekanntheit und Vertrauen'
        : 'Higher brand awareness and trust'
    });
    
    return opportunities;
  },

  /**
   * Generate recommendations based on pain points and opportunities
   */
  async generateRecommendations(
    ci: CustomerIntelligence,
    language: 'de' | 'en',
    focusAreas?: string[]
  ): Promise<Recommendation[]> {
    const recommendations: Recommendation[] = [];
    
    // Google Ads recommendation
    if (!focusAreas || focusAreas.includes('google-ads')) {
      recommendations.push({
        service: 'Google Ads Management',
        description: language === 'de'
          ? 'Professionelle Kampagnenerstellung und -optimierung mit KI-Unterstützung'
          : 'Professional campaign creation and optimization with AI support',
        benefits: language === 'de'
          ? ['Zielgruppenspezifische Anzeigen', 'Kontinuierliche Optimierung', 'Transparentes Reporting']
          : ['Audience-specific ads', 'Continuous optimization', 'Transparent reporting'],
        estimatedImpact: language === 'de'
          ? 'ROI-Steigerung um 25-40%'
          : '25-40% ROI increase',
        priority: 'immediate'
      });
    }
    
    // Technical optimization if needed
    if (ci.performance?.pageSpeed?.performanceScore && ci.performance.pageSpeed.performanceScore < 70) {
      recommendations.push({
        service: language === 'de' ? 'Website-Performance-Optimierung' : 'Website Performance Optimization',
        description: language === 'de'
          ? 'Technische Optimierung für schnellere Ladezeiten und bessere Core Web Vitals'
          : 'Technical optimization for faster load times and better Core Web Vitals',
        benefits: language === 'de'
          ? ['Bessere SEO-Rankings', 'Höhere Conversion-Rate', 'Verbesserte User Experience']
          : ['Better SEO rankings', 'Higher conversion rate', 'Improved user experience'],
        estimatedImpact: language === 'de'
          ? 'Performance-Score von ${ci.performance.pageSpeed.performanceScore} auf 80+'
          : `Performance score from ${ci.performance.pageSpeed.performanceScore} to 80+`,
        priority: 'short-term'
      });
    }
    
    // Branding recommendation
    if (!focusAreas || focusAreas.includes('branding')) {
      recommendations.push({
        service: language === 'de' ? 'Brand-Strategie' : 'Brand Strategy',
        description: language === 'de'
          ? 'Entwicklung einer konsistenten Markenidentität und Kommunikationsstrategie'
          : 'Development of a consistent brand identity and communication strategy',
        benefits: language === 'de'
          ? ['Klare Markenpositionierung', 'Einheitliche Kommunikation', 'Stärkere Kundenbindung']
          : ['Clear brand positioning', 'Unified communication', 'Stronger customer loyalty'],
        estimatedImpact: language === 'de'
          ? 'Gesteigerte Markenbekanntheit'
          : 'Increased brand awareness',
        priority: 'long-term'
      });
    }
    
    return recommendations;
  },

  /**
   * Generate executive summary using AI
   */
  async generateExecutiveSummary(
    ci: CustomerIntelligence,
    painPoints: PainPoint[],
    opportunities: Opportunity[],
    language: 'de' | 'en'
  ): Promise<string> {
    const prompt = language === 'de' 
      ? `Erstelle eine kurze Executive Summary (3-4 Sätze) für ein Verkaufsgespräch mit ${ci.businessName}:

Branche: ${ci.brand.industry}
Identifizierte Pain Points: ${painPoints.map(p => p.title).join(', ')}
Hauptchancen: ${opportunities.map(o => o.title).join(', ')}

Die Summary soll professionell, aber zugänglich sein und den Mehrwert für den Kunden hervorheben.`
      : `Create a brief executive summary (3-4 sentences) for a sales conversation with ${ci.businessName}:

Industry: ${ci.brand.industry}
Identified Pain Points: ${painPoints.map(p => p.title).join(', ')}
Main Opportunities: ${opportunities.map(o => o.title).join(', ')}

The summary should be professional yet approachable and highlight the value for the customer.`;

    try {
      const response = await geminiService.generateWithGemini(prompt, {
        maxOutputTokens: 500
      });
      return response;
    } catch (e) {
      // Fallback summary
      return language === 'de'
        ? `${ci.businessName} ist ein Unternehmen in der ${ci.brand.industry}-Branche mit Potenzial für digitale Optimierung. Unsere Analyse zeigt ${painPoints.length} Verbesserungsbereiche und ${opportunities.length} Wachstumschancen.`
        : `${ci.businessName} is a company in the ${ci.brand.industry} industry with potential for digital optimization. Our analysis shows ${painPoints.length} areas for improvement and ${opportunities.length} growth opportunities.`;
    }
  },

  /**
   * Build brand analysis from CI data
   */
  buildBrandAnalysis(ci: CustomerIntelligence): SalesDossier['brandAnalysis'] {
    return {
      currentVoice: ci.voice?.formality === 'formal' 
        ? 'Formell und professionell' 
        : ci.voice?.formality === 'informal' 
          ? 'Locker und zugänglich'
          : 'Neutral',
      visualIdentity: ci.visual?.primaryColor 
        ? `Primärfarbe: ${ci.visual.primaryColor}` 
        : 'Nicht analysiert',
      strengths: [
        ci.brand.usp || 'Einzigartiges Angebot',
        ...(ci.brand.keyBenefits?.slice(0, 2) || [])
      ],
      improvementAreas: [
        ...(ci.voice?.dontStatements?.slice(0, 2) || ['Konsistenz verbessern'])
      ]
    };
  },

  /**
   * Build competitive insights from CI data
   */
  buildCompetitiveInsights(ci: CustomerIntelligence): SalesDossier['competitiveInsights'] {
    const competitors = ci.competitors || [];
    return {
      competitors: competitors.slice(0, 5).map((c: any) => c.name || c),
      marketPosition: competitors.length > 3 
        ? 'Wettbewerbsintensiver Markt' 
        : 'Moderater Wettbewerb',
      differentiators: ci.brand.keyBenefits?.slice(0, 3) || []
    };
  },

  /**
   * Generate example assets (ad copy, image prompts)
   */
  async generateExampleAssets(
    ci: CustomerIntelligence,
    language: 'de' | 'en'
  ): Promise<SalesDossier['exampleAssets']> {
    const prompt = language === 'de'
      ? `Generiere für ${ci.businessName} (${ci.brand.industry}):
1. 5 Google Ads Headlines (max 30 Zeichen)
2. 2 Google Ads Descriptions (max 90 Zeichen)
3. 3 Bildprompts für brand-konsistente Bilder

USP: ${ci.brand.usp}
Tonalität: ${ci.voice?.formality || 'professionell'}

Antwort als JSON: { "headlines": [], "descriptions": [], "imagePrompts": [] }`
      : `Generate for ${ci.businessName} (${ci.brand.industry}):
1. 5 Google Ads Headlines (max 30 chars)
2. 2 Google Ads Descriptions (max 90 chars)
3. 3 Image prompts for brand-consistent images

USP: ${ci.brand.usp}
Tone: ${ci.voice?.formality || 'professional'}

Response as JSON: { "headlines": [], "descriptions": [], "imagePrompts": [] }`;

    try {
      const response = await geminiService.generateJSON(prompt);
      return {
        adCopy: {
          headlines: response.headlines || [],
          descriptions: response.descriptions || []
        },
        imagePrompts: response.imagePrompts || []
      };
    } catch (e) {
      console.warn('Failed to generate example assets:', e);
      return undefined;
    }
  },

  /**
   * Generate dossier from URL (convenience method)
   */
  async generateDossierFromUrl(
    orgId: string,
    url: string,
    userId: string,
    options: DossierOptions = {}
  ): Promise<SalesDossier> {
    // First ensure CI exists
    let ci = await customerIntelligenceService.findByDomain(orgId, url);
    
    if (!ci) {
      ci = await customerIntelligenceService.analyzeProspect(orgId, url, userId);
    }
    
    return this.generateDossier(orgId, ci.id, options);
  }
};

export default salesDossierService;

